import clsx from "clsx";
import { useRouter } from "next/router";
import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import CountryNumber from "@components/CountryNumber/CountryNumber";
import FormField from "@components/Form/FormFields/FormFields";
import FormPrivacySection from "@components/FormPrivacySection/FormPrivacySection";
import Grid from "@components/Grid/Grid";
import { useSettings } from "@utilities/context/settings";
import { TypeConsultantFormFields } from "types/contentful/TypeConsultantForm";
import { getPersonalDetails, postContactForm } from "../../services/forms";
import { countryCodesData } from "../CountryNumber/CountryCodes";

import FormErrorMessage from "@components/FormErrorMessage/FormErrorMessage";
import SubmitButton from "@components/SubmitButton/SubmitButton";
import { handleSubmitFormErrors } from "@utilities/handleSubmitFormErrors";
import { UserContext } from "../../context/user";

import { getBusinessUnit } from "@utilities/getBusinessUnit";
import getCookie from "@utilities/getCookie";
import setGADataLayer from "@utilities/setGADataLayer";
import styles from "./ConsultantForm.module.scss";

import SmsConsent from "@components/SMSConsentCheckbox";
import { getRedirectUrl } from "../../utils/redirects/sitecoreRedirects";

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

let firstInteract = 0;

export const ConsultantForm = (props: {
  content: TypeConsultantFormFields;
}) => {
  const { content } = props;
  const {
    consultantFormTitleText,
    introText = `Please complete the form below with details of your request.
                  We’ll get back to you by email, so make sure you include the
                  right email address.`,
    introLoggedInText,
    emailLabelText,
    firstNameLabelText,
    lastNameLabelText,
    countryCodeLabelText,
    telephoneNumberPlaceholderText,
    telephoneNumberHintText,
    commentEnquiryLabelText,
    successPageUrl,
    failedMessage,
    smsConsentText,
    programmesForSmsConsent,
  } = content;

  const [isSMSConsentChecked, setIsSMSConsentChecked] = useState<boolean>(null);
  const [showSMSConsent, setShowSMSConsent] = useState<boolean>(false);
  const [userPersonalDetails, setUserPersonalDetails] = useState<any>(null);
  const [selectedDialCode, setSelectedDialCode] = useState("");
  const [numberValue, setNumberValue] = useState("");
  const [error, setHasApiError] = useState<boolean>(false);
  const [handledSubmitFormErrors, setHandledSubmitFormErrors] =
    useState<Array<string> | null>(null);
  const [loggedInUser, setLoggedInUser] = useState<boolean | null>(null);
  const [authToken, setAuthToken] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [canContactBySMS, setCanContactBySMS] = useState<boolean>(false);

  const router = useRouter();
  const { user } = useContext(UserContext);
  const genuinePath = router?.query?.referPage || router?.asPath;

  const {
    register,
    handleSubmit,
    reset,
    formState,
    watch,
    setValue,
    setError,
    clearErrors,
  } = useForm({
    mode: "onBlur",
  });

  const isWindow = typeof window === "object";
  const urlParams = isWindow
    ? new URLSearchParams(window.location.search)
    : null;
  const pathname = isWindow ? window.location.pathname : null;

  const itemId = urlParams?.get("itemId");

  if (itemId !== null) {
    const sitecoreRedirectUrl = getRedirectUrl(pathname, urlParams);

    if (sitecoreRedirectUrl !== null) {
      router.push(sitecoreRedirectUrl);
    }
  }

  const { apiErrorMessages } = useSettings();

  useEffect(() => {
    if (user) {
      setLoggedInUser(true);
    } else {
      setLoggedInUser(null);
    }
    if (user) {
      setAuthToken(getCookie("access_token"));
    }
  }, [user]);

  useEffect(() => {
    if (loggedInUser) {
      const contactUsFormData = async () => {
        try {
          const userPersonalDetails = await getPersonalDetails();
          setUserPersonalDetails(userPersonalDetails);
          setCanContactBySMS(
            userPersonalDetails?.result?.preferences?.canContactBySms
          );
        } catch (_e) {
          setHasApiError(true);
        }
      };

      contactUsFormData();
    }
  }, [loggedInUser]);

  useEffect(() => {
    programmesForSmsConsent?.forEach((programme) => {
      if (
        router?.query?.programmeTypeCode == programme.fields.programmeTypeCode
      ) {
        setShowSMSConsent(true);
      }
    });
  }, [router?.query]);

  const FormFields = [
    {
      propertyName: "emailAddress",
      validation: {
        isRequired: true,
        email: true,
        maxLength: 100,
      },
      labelText: emailLabelText,
      dataText: userPersonalDetails?.result?.emailAddress,
    },
    {
      propertyName: "firstName",
      validation: {
        isRequired: true,
        maxLength: 30,
        firstName: true,
      },
      labelText: firstNameLabelText,
      dataText: userPersonalDetails?.result?.firstName,
    },
    {
      propertyName: "lastName",
      validation: {
        isRequired: true,
        maxLength: 30,
        lastName: true,
      },
      labelText: lastNameLabelText,
      dataText: userPersonalDetails?.result?.lastName,
    },
    {
      propertyName: "telephone",
      labelText: "",
      hintText: "",
      optionalText: false,
      options: null,
    },
    {
      propertyName: "smsConsent",
      labelText: "",
      hintText: "",
      optionalText: false,
      validation: {
        isRequired: false,
      },
    },
    {
      propertyName: "objectivesText",
      validation: {
        isRequired: true,
        maxLength: 140,
        textarea: true,
      },
      labelText: commentEnquiryLabelText,
      formType: "textarea",
    },
  ];

  useEffect(() => {
    const subscription = watch((_e, a) => {
      if (a.type === "change" && firstInteract < 1) {
        setGADataLayer({
          event: "formStart",
          formName: "consultantForm",
        });
        firstInteract++;
        return () => subscription.unsubscribe();
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (userPersonalDetails) {
      const inputProp = [...FormFields].reduce(
        (o, key: any) => ({ ...o, [key.propertyName]: key.dataText }),
        {}
      );
      reset(inputProp);
    }
  }, [userPersonalDetails]);

  const redirectToSuccessPage = () => {
    setGADataLayer({
      event: "formComplete",
      formName: "consultantForm",
      businessUnit: getBusinessUnit(),
      programmeCode: "",
      eventType: "",
    });

    const fullSuccessPageUrl: string =
      "/" + successPageUrl + "?returnToPreviousPageUrl=" + genuinePath;

    router.push(fullSuccessPageUrl);
  };

  const onSubmit = async (data) => {
    setHandledSubmitFormErrors(null);
    setLoading(true);

    const {
      emailAddress,
      firstName,
      lastName,
      telephone,
      noBulkEmail,
      objectivesText,
    } = data;

    // API expects digits-only phone number, so we strip out any other characters
    // Field is optional, so we send null if no number provided
    const telephoneDigits = (telephone || "").replace(/\D/g, "");
    const telephoneToSend = telephoneDigits || null;

    const formPayloadData = {
      profile: {
        isStayInformed: noBulkEmail,
        emailAddress,
        firstName,
        lastName,
      },
      programmeType: {
        programmeTypeCode: router?.query?.programmeTypeCode || null,
        programmeTypeGroup: router?.query?.programmeTypeGroup || null,
      },
      telephoneNumber: telephoneToSend,
      preferences: {
        canContactBySms: isSMSConsentChecked || null,
      },
      comments: objectivesText,
    };

    try {
      const response = await postContactForm(
        formPayloadData,
        sessionStorage.getItem("crm_campaign")
      );

      setLoading(false);

      if (response.status === 201) {
        redirectToSuccessPage();
      } else {
        const handledSubmitFormErrors = handleSubmitFormErrors(
          response,
          apiErrorMessages
        );
        window.scrollTo(0, 0);
        setHandledSubmitFormErrors(handledSubmitFormErrors);
      }
      setLoading(false);
    } catch (_e) {
      const handledSubmitFormErrors = handleSubmitFormErrors(
        null,
        apiErrorMessages
      );
      window.scrollTo(0, 0);
      setHandledSubmitFormErrors(handledSubmitFormErrors);
      setLoading(false);
    }
  };

  return (
    <div className="wrapper component-wrap">
      <Grid row>
        <Grid column sm={12} md={6}>
          <section
            className={clsx(
              styles.component,
              styles["consult-form"],
              "form",
              styles.cf
            )}
          >
            <h1 className="h2">{consultantFormTitleText}</h1>
            {error && (
              <FormErrorMessage
                failedRichText={failedMessage?.fields?.content}
                failedTinyMceRichText={failedMessage?.fields?.contentTinyMce}
              />
            )}
            {handledSubmitFormErrors &&
              handledSubmitFormErrors.map((error, index) => (
                <FormErrorMessage key={index} text={error} />
              ))}
            <form method="POST" className="form" onSubmit={handleSubmit(onSubmit)}>
              <div className={styles["fields-floated"]}>
                <p className={styles.intro}>
                  {loggedInUser && introLoggedInText
                    ? introLoggedInText
                    : introText}
                </p>

                <div className="form-row">
                  <section className={styles["form-group-wrapper"]}>
                    {FormFields.map((field: any, index) => {
                      if (field.propertyName === "telephone") {
                        return (
                          <CountryNumber
                            key={`${countryCodeLabelText}${index}`}
                            dialCodeLabelText={countryCodeLabelText}
                            telephoneInputPlaceholderText={
                              telephoneNumberPlaceholderText
                            }
                            telephoneNumberHintText={telephoneNumberHintText}
                            optionsData={countryCodesData}
                            selectedDialCode={selectedDialCode}
                            setSelectedDialCode={setSelectedDialCode}
                            numberValue={numberValue}
                            setNumberValue={setNumberValue}
                            register={register}
                            watch={watch}
                            errors={formState.errors}
                            fieldIsRequired={false}
                            setValue={setValue}
                            setError={setError}
                            clearErrors={clearErrors}
                          />
                        );
                      }
                      if (field.propertyName === "smsConsent") {
                        return (
                          <SmsConsent
                            key={`${field.labelText}${index}`}
                            showSMSConsent={showSMSConsent}
                            field={field}
                            index={index}
                            smsConsentText={smsConsentText}
                            smsConsentGiven={canContactBySMS}
                            isSMSconsentChecked={isSMSConsentChecked}
                            setIsSMSconsentChecked={setIsSMSConsentChecked}
                            register={register}
                          />
                        );
                      }
                      return (
                        <FormField
                          formType={field?.formType}
                          watch={watch}
                          validation={field?.validation}
                          type="text"
                          register={register}
                          property={field.dataText}
                          key={`${field.labelText}${index}`}
                          hintText={field?.hintText}
                          placeholder={field.labelText}
                          options={field.options}
                          optionalText={field?.optionalText}
                          name={field?.propertyName}
                          errors={formState.errors}
                        />
                      );
                    })}
                  </section>
                </div>

                <FormPrivacySection
                  register={register}
                  formState={formState}
                  chosenEmailSubscription={
                    loggedInUser && userPersonalDetails?.result?.isStayInformed
                  }
                  isEmailAndSMSOptOut={showSMSConsent}
                />
                <div className="btn-and-text-wrapper">
                  <SubmitButton
                    loading={loading}
                    id="submitButton"
                    text="Submit"
                  />
                </div>
              </div>
            </form>
          </section>
        </Grid>
      </Grid>
    </div>
  );
};

export default ConsultantForm;
